import { useContext } from 'react';
import { useSelector } from 'react-redux';

import { createId } from '@paralleldrive/cuid2';

import { selectSizesViewed } from '@/store/bookingSlice';

import { usePostTrackEventMutation } from '../api/rapidstor';
import Logger from '../utils/logging';
import getOrCreateSessionId from '../utils/session';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

/**
 * Anything JSON can carry. The server stores `extraData` as a free-form object
 * (`core/tracking.ts`), so the only real constraint is that it serialises.
 */
export type TrackEventValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | TrackEventValue[]
    | { [key: string]: TrackEventValue };

export type TrackEventData = Record<string, TrackEventValue>;

export default function useRapidStorTracking() {
    const [postTrackEvent] = usePostTrackEventMutation();
    const sizesViewed = useSelector(selectSizesViewed);
    const { persona } = useContext(JourneyContext);

    const trackEvent = (
        eventName: string,
        eventData: TrackEventData,
    ) => {
        try {
            const {
                locationToken,
                corpToken,
                variant,
                unitTypeRegex,
                unitTypeNames,
            } = window.rapidstor_settings.rapidstor_options;
            const trackingId = createId();
            const timestamp = new Date().toISOString();
            const { userAgent } = window.navigator;
            const referrerUrl = document.referrer;
            const url = window.location.href;

            const userUuid = localStorage.getItem('rapidstor-user-uuid');
            const configUuid = localStorage.getItem('rapidstor-config-uuid');

            const extraData = {
                ...eventData,
                variant,
                userUuid,
                configUuid,
                persona: persona ?? undefined,
                unitTypeRegex: unitTypeRegex ?? undefined,
                unitTypeNames: unitTypeNames ?? undefined,
            };

            const payload = {
                locationToken,
                corpToken,
                payload: {
                    trackingId,
                    sessionId: getOrCreateSessionId(),
                    action: eventName,
                    timestamp,
                    url,
                    referrerUrl,
                    userAgent,
                    extraData,
                },
            };

            postTrackEvent(payload);
        } catch (error) {
            Logger.error(`Error tracking event: ${JSON.stringify(error)}`);
        }
    };

    const trackSizesViewed = () => {
        try {
            trackEvent('unit_sizes_viewed', {
                sizes: sizesViewed,
            });
        } catch (error) {
            Logger.error(`Error tracking sizes viewed: ${JSON.stringify(error)}`);
        }
    };

    return { trackEvent, trackSizesViewed };
}
